import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Components
import Text from "new_components/atoms/Text";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";

// Container for all sections
const PillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 0;
`;

// Section wrapper
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  /* Make outcomes section more prominent */
  ${({ isOutcomes }) => isOutcomes && `
    border: 1px solid ${colors.primary[200]};
    border-radius: 12px;
    background-color: ${colors.primary[25]};
    padding: 12px 0;
    margin: 0;
  `}
`;

// Section header
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

// Pills container for each section
const PillsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Individual pill
const Pill = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  border: 1px solid ${({ isSelected }) => 
    isSelected ? colors.primary[500] : colors.neutral[300]};
  background-color: ${({ isSelected }) => 
    isSelected ? colors.primary[50] : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 280px;

  &:hover {
    border-color: ${colors.primary[400]};
    background-color: ${({ isSelected }) => 
      isSelected ? colors.primary[100] : colors.primary[25]};
  }

  &:active {
    transform: translateY(1px);
  }
`;

// Pill text
const PillText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

// Icon wrapper
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const DataSourcePills = ({ 
  dataSourceConfig = [], 
  selectedSources = [],
  onSourceToggle 
}) => {
  
  // Check if a source is selected
  const isSourceSelected = (source, sectionValue) => {
    return selectedSources.some(selected => {
      if (sectionValue === 'pulse') {
        return selected.type === 'pulse' && selected.id === source.id;
      } else if (sectionValue === 'outcome') {
        return selected.type === 'outcome' && selected.id === source.value.id;
      } else if (sectionValue === 'culture') {
        return selected.type === 'culture' && selected.id === source.value.id;
      } else if (sectionValue === 'custom') {
        return selected.type === 'custom' && selected.id === source.value.id;
      }
      return false;
    });
  };

  // Handle pill click
  const handlePillClick = (source, section) => {
    if (onSourceToggle) {
      onSourceToggle(source, section);
    }
  };

  return (
    <PillsContainer>
      {dataSourceConfig.map((section) => {
        if (!section.options || section.options.length === 0) return null;
        
        return (
          <Section key={section.value} isOutcomes={section.value === 'outcome'}>
            <SectionHeader isOutcomes={section.value === 'outcome'}>
              <Text typographyStyle={typography.body.semibold}>
                {section.label}
              </Text>
              <Text 
                typographyStyle={typography.smallText.regular}
                color={colors.text.gray}
              >
                ({section.options.length} available)
              </Text>
            </SectionHeader>
            
            <PillsGrid isOutcomes={section.value === 'outcome'}>
              {section.options.map((option, index) => {
                const isSelected = isSourceSelected(option, section.value);
                const displayText = option.label || option.name || option.task_name || 'Unnamed';
                
                return (
                  <Pill
                    key={option.id || option.value?.id || index}
                    isSelected={isSelected}
                    onClick={() => handlePillClick(option, section)}
                  >
                    <PillText 
                      typographyStyle={typography.smallText.semibold}
                      color={isSelected ? colors.primary[700] : colors.text.dark}
                    >
                      {displayText}
                    </PillText>
                    <IconWrapper>
                      <Icon
                        name={isSelected ? ICON_SET.check : ICON_SET.add}
                        size={14}
                        color={isSelected ? colors.primary[600] : colors.neutral[500]}
                      />
                    </IconWrapper>
                  </Pill>
                );
              })}
            </PillsGrid>
          </Section>
        );
      })}
    </PillsContainer>
  );
};

DataSourcePills.propTypes = {
  dataSourceConfig: PropTypes.array,
  selectedSources: PropTypes.array,
  onSourceToggle: PropTypes.func,
};

export default DataSourcePills; 